import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api';

function normalize(url: string): string {
  return url.replace(/\/+$/, '');
}

interface AdminCabinetUrlContextValue {
  url: string;
  isLoading: boolean;
  setUrl: (url: string) => void;
  refresh: () => Promise<void>;
  buildShopUrl: (storeId: string, path?: string) => string;
}

const AdminCabinetUrlContext = createContext<AdminCabinetUrlContextValue | null>(null);

// Источник правды — `client_settings.pimmer_settings.adminCabinetUrl` (через
// GET /client-settings/pimmer). Управляется в карточке «Pimmer API» на странице /services.
export function AdminCabinetUrlProvider({ children }: { children: React.ReactNode }) {
  const [url, setUrlState] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const settings = await api.getPimmerSettings();
      setUrlState(normalize(settings.adminCabinetUrl || ''));
    } catch {
      setUrlState('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setUrl = useCallback((value: string) => {
    setUrlState(normalize(value || ''));
  }, []);

  const buildShopUrl = useCallback(
    (storeId: string, path = '') => {
      if (!url) return '';
      const safePath = path && !path.startsWith('/') ? `/${path}` : path;
      return `${url}/shop/${storeId}${safePath}`;
    },
    [url]
  );

  return (
    <AdminCabinetUrlContext.Provider value={{ url, isLoading, setUrl, refresh, buildShopUrl }}>
      {children}
    </AdminCabinetUrlContext.Provider>
  );
}

export function useAdminCabinetUrl(): AdminCabinetUrlContextValue {
  const ctx = useContext(AdminCabinetUrlContext);
  if (!ctx) throw new Error('useAdminCabinetUrl must be used within AdminCabinetUrlProvider');
  return ctx;
}
